import * as z from 'zod';

export const CreateCategoryRequest = z.object({
  category: z
    .object({
      _id: z.string().describe('Category ID.').max(38).optional(),
      label: z
        .string()
        .describe('Category label. Displayed in the Category Menu.')
        .max(35),
      postCount: z
        .number()
        .int()
        .describe('Number of posts in the category.')
        .optional(),
      url: z
        .string()
        .describe(
          'The `url` of the page that lists every post with the specified category.'
        )
        .optional(),
      description: z
        .string()
        .describe('Category description.')
        .max(500)
        .optional()
        .nullable(),
      title: z.string().describe('Category title.').max(200).optional(),
      displayPosition: z
        .number()
        .int()
        .describe(
          'Position of the category in the [Category Menu](https://support.wix.com/en/article/wix-blog-adding-and-customizing-a-category-menu).\nCategories are displayed in ascending order. Categories with a position of `-1` appear at the end of the sequence.\n\nDefault: `-1`'
        )
        .min(0)
        .optional()
        .nullable(),
      translationId: z
        .string()
        .describe(
          "ID of the category's translations. All translations of a single category share the same `translationId`."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      language: z
        .string()
        .describe(
          'Category language.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
        )
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe(
          "Part of a category's URL that refers to a specific category. For example, the slug of `https:/example.com/blog/category/famous-cats` is `famous-cats`."
        )
        .max(100)
        .optional(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('SEO data.')
        .optional(),
      coverImage: z.string().describe('Category cover image.').optional(),
      _updatedDate: z
        .date()
        .describe('Date and time the Category was last updated.')
        .optional()
        .nullable(),
    })
    .describe('Category info.'),
  options: z
    .object({
      fieldsets: z
        .array(z.enum(['UNKNOWN', 'URL', 'SEO']))
        .max(10)
        .optional(),
    })
    .optional(),
});
export const CreateCategoryResponse = z.object({
  _id: z.string().describe('Category ID.').max(38).optional(),
  label: z
    .string()
    .describe('Category label. Displayed in the Category Menu.')
    .max(35)
    .optional(),
  postCount: z
    .number()
    .int()
    .describe('Number of posts in the category.')
    .optional(),
  url: z
    .string()
    .describe(
      'The `url` of the page that lists every post with the specified category.'
    )
    .optional(),
  description: z
    .string()
    .describe('Category description.')
    .max(500)
    .optional()
    .nullable(),
  title: z.string().describe('Category title.').max(200).optional(),
  displayPosition: z
    .number()
    .int()
    .describe(
      'Position of the category in the [Category Menu](https://support.wix.com/en/article/wix-blog-adding-and-customizing-a-category-menu).\nCategories are displayed in ascending order. Categories with a position of `-1` appear at the end of the sequence.\n\nDefault: `-1`'
    )
    .min(0)
    .optional()
    .nullable(),
  translationId: z
    .string()
    .describe(
      "ID of the category's translations. All translations of a single category share the same `translationId`."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  language: z
    .string()
    .describe(
      'Category language.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
    )
    .optional()
    .nullable(),
  slug: z
    .string()
    .describe(
      "Part of a category's URL that refers to a specific category. For example, the slug of `https:/example.com/blog/category/famous-cats` is `famous-cats`."
    )
    .max(100)
    .optional(),
  seoData: z
    .object({
      tags: z
        .array(
          z.object({
            type: z
              .string()
              .describe(
                'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
              )
              .optional(),
            props: z
              .record(z.string(), z.any())
              .describe(
                'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
              )
              .optional()
              .nullable(),
            meta: z
              .record(z.string(), z.any())
              .describe(
                'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
              )
              .optional()
              .nullable(),
            children: z
              .string()
              .describe(
                'SEO tag inner content. For example, `<title> inner content </title>`.'
              )
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
              )
              .optional(),
            disabled: z
              .boolean()
              .describe(
                "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
              )
              .optional(),
          })
        )
        .optional(),
      settings: z
        .object({
          preventAutoRedirect: z
            .boolean()
            .describe(
              'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
            )
            .optional(),
          keywords: z
            .array(
              z.object({
                term: z.string().describe('Keyword value.').optional(),
                isMain: z
                  .boolean()
                  .describe('Whether the keyword is the main focus keyword.')
                  .optional(),
                origin: z
                  .string()
                  .describe(
                    'The source that added the keyword terms to the SEO settings.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .max(5)
            .optional(),
        })
        .describe('SEO general settings.')
        .optional(),
    })
    .describe('SEO data.')
    .optional(),
  coverImage: z.string().describe('Category cover image.').optional(),
  _updatedDate: z
    .date()
    .describe('Date and time the Category was last updated.')
    .optional()
    .nullable(),
});
export const UpdateCategoryRequest = z.object({
  _id: z.string().describe('Category ID.').max(38),
  category: z
    .object({
      _id: z.string().describe('Category ID.').max(38).optional(),
      label: z
        .string()
        .describe('Category label. Displayed in the Category Menu.')
        .max(35)
        .optional(),
      postCount: z
        .number()
        .int()
        .describe('Number of posts in the category.')
        .optional(),
      url: z
        .string()
        .describe(
          'The `url` of the page that lists every post with the specified category.'
        )
        .optional(),
      description: z
        .string()
        .describe('Category description.')
        .max(500)
        .optional()
        .nullable(),
      title: z.string().describe('Category title.').max(200).optional(),
      displayPosition: z
        .number()
        .int()
        .describe(
          'Position of the category in the [Category Menu](https://support.wix.com/en/article/wix-blog-adding-and-customizing-a-category-menu).\nCategories are displayed in ascending order. Categories with a position of `-1` appear at the end of the sequence.\n\nDefault: `-1`'
        )
        .min(0)
        .optional()
        .nullable(),
      translationId: z
        .string()
        .describe(
          "ID of the category's translations. All translations of a single category share the same `translationId`."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      language: z
        .string()
        .describe(
          'Category language.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
        )
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe(
          "Part of a category's URL that refers to a specific category. For example, the slug of `https:/example.com/blog/category/famous-cats` is `famous-cats`."
        )
        .max(100)
        .optional(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('SEO data.')
        .optional(),
      coverImage: z.string().describe('Category cover image.').optional(),
      _updatedDate: z
        .date()
        .describe('Date and time the Category was last updated.')
        .optional()
        .nullable(),
    })
    .describe('Category info.'),
  options: z
    .object({
      fieldsets: z
        .array(z.enum(['UNKNOWN', 'URL', 'SEO']))
        .max(10)
        .optional(),
    })
    .optional(),
});
export const UpdateCategoryResponse = z.object({
  _id: z.string().describe('Category ID.').max(38).optional(),
  label: z
    .string()
    .describe('Category label. Displayed in the Category Menu.')
    .max(35)
    .optional(),
  postCount: z
    .number()
    .int()
    .describe('Number of posts in the category.')
    .optional(),
  url: z
    .string()
    .describe(
      'The `url` of the page that lists every post with the specified category.'
    )
    .optional(),
  description: z
    .string()
    .describe('Category description.')
    .max(500)
    .optional()
    .nullable(),
  title: z.string().describe('Category title.').max(200).optional(),
  displayPosition: z
    .number()
    .int()
    .describe(
      'Position of the category in the [Category Menu](https://support.wix.com/en/article/wix-blog-adding-and-customizing-a-category-menu).\nCategories are displayed in ascending order. Categories with a position of `-1` appear at the end of the sequence.\n\nDefault: `-1`'
    )
    .min(0)
    .optional()
    .nullable(),
  translationId: z
    .string()
    .describe(
      "ID of the category's translations. All translations of a single category share the same `translationId`."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  language: z
    .string()
    .describe(
      'Category language.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
    )
    .optional()
    .nullable(),
  slug: z
    .string()
    .describe(
      "Part of a category's URL that refers to a specific category. For example, the slug of `https:/example.com/blog/category/famous-cats` is `famous-cats`."
    )
    .max(100)
    .optional(),
  seoData: z
    .object({
      tags: z
        .array(
          z.object({
            type: z
              .string()
              .describe(
                'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
              )
              .optional(),
            props: z
              .record(z.string(), z.any())
              .describe(
                'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
              )
              .optional()
              .nullable(),
            meta: z
              .record(z.string(), z.any())
              .describe(
                'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
              )
              .optional()
              .nullable(),
            children: z
              .string()
              .describe(
                'SEO tag inner content. For example, `<title> inner content </title>`.'
              )
              .optional(),
            custom: z
              .boolean()
              .describe(
                'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
              )
              .optional(),
            disabled: z
              .boolean()
              .describe(
                "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
              )
              .optional(),
          })
        )
        .optional(),
      settings: z
        .object({
          preventAutoRedirect: z
            .boolean()
            .describe(
              'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
            )
            .optional(),
          keywords: z
            .array(
              z.object({
                term: z.string().describe('Keyword value.').optional(),
                isMain: z
                  .boolean()
                  .describe('Whether the keyword is the main focus keyword.')
                  .optional(),
                origin: z
                  .string()
                  .describe(
                    'The source that added the keyword terms to the SEO settings.'
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .max(5)
            .optional(),
        })
        .describe('SEO general settings.')
        .optional(),
    })
    .describe('SEO data.')
    .optional(),
  coverImage: z.string().describe('Category cover image.').optional(),
  _updatedDate: z
    .date()
    .describe('Date and time the Category was last updated.')
    .optional()
    .nullable(),
});
export const GetCategoryRequest = z.object({
  categoryId: z.string().describe('Category ID.').min(1).max(38),
  options: z
    .object({
      fieldsets: z
        .array(z.enum(['UNKNOWN', 'URL', 'SEO']))
        .max(10)
        .optional(),
    })
    .describe('Options specifying which fields to return.')
    .optional(),
});
export const GetCategoryResponse = z.object({
  category: z
    .object({
      _id: z.string().describe('Category ID.').max(38).optional(),
      label: z
        .string()
        .describe('Category label. Displayed in the Category Menu.')
        .max(35)
        .optional(),
      postCount: z
        .number()
        .int()
        .describe('Number of posts in the category.')
        .optional(),
      url: z
        .string()
        .describe(
          'The `url` of the page that lists every post with the specified category.'
        )
        .optional(),
      description: z
        .string()
        .describe('Category description.')
        .max(500)
        .optional()
        .nullable(),
      title: z.string().describe('Category title.').max(200).optional(),
      displayPosition: z
        .number()
        .int()
        .describe(
          'Position of the category in the [Category Menu](https://support.wix.com/en/article/wix-blog-adding-and-customizing-a-category-menu).\nCategories are displayed in ascending order. Categories with a position of `-1` appear at the end of the sequence.\n\nDefault: `-1`'
        )
        .min(0)
        .optional()
        .nullable(),
      translationId: z
        .string()
        .describe(
          "ID of the category's translations. All translations of a single category share the same `translationId`."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      language: z
        .string()
        .describe(
          'Category language.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
        )
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe(
          "Part of a category's URL that refers to a specific category. For example, the slug of `https:/example.com/blog/category/famous-cats` is `famous-cats`."
        )
        .max(100)
        .optional(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('SEO data.')
        .optional(),
      coverImage: z.string().describe('Category cover image.').optional(),
      _updatedDate: z
        .date()
        .describe('Date and time the Category was last updated.')
        .optional()
        .nullable(),
    })
    .describe('Category info.')
    .optional(),
});
export const GetCategoryBySlugRequest = z.object({
  slug: z
    .string()
    .describe(
      "Slug of the category to retrieve. \n\nThe end of a category's URL. For example, `'https:/example.com/blog/category/{my-category-slug}'`. Case sensitive and generally based on the category `label` if not specified."
    )
    .min(1)
    .max(100),
  options: z
    .object({
      fieldsets: z
        .array(z.enum(['UNKNOWN', 'URL', 'SEO']))
        .max(10)
        .optional(),
      language: z
        .string()
        .describe(
          '2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nLanguage of the category to retrieve.'
        )
        .optional()
        .nullable(),
    })
    .describe('Options specifying which fields to return.')
    .optional(),
});
export const GetCategoryBySlugResponse = z.object({
  category: z
    .object({
      _id: z.string().describe('Category ID.').max(38).optional(),
      label: z
        .string()
        .describe('Category label. Displayed in the Category Menu.')
        .max(35)
        .optional(),
      postCount: z
        .number()
        .int()
        .describe('Number of posts in the category.')
        .optional(),
      url: z
        .string()
        .describe(
          'The `url` of the page that lists every post with the specified category.'
        )
        .optional(),
      description: z
        .string()
        .describe('Category description.')
        .max(500)
        .optional()
        .nullable(),
      title: z.string().describe('Category title.').max(200).optional(),
      displayPosition: z
        .number()
        .int()
        .describe(
          'Position of the category in the [Category Menu](https://support.wix.com/en/article/wix-blog-adding-and-customizing-a-category-menu).\nCategories are displayed in ascending order. Categories with a position of `-1` appear at the end of the sequence.\n\nDefault: `-1`'
        )
        .min(0)
        .optional()
        .nullable(),
      translationId: z
        .string()
        .describe(
          "ID of the category's translations. All translations of a single category share the same `translationId`."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      language: z
        .string()
        .describe(
          'Category language.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
        )
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe(
          "Part of a category's URL that refers to a specific category. For example, the slug of `https:/example.com/blog/category/famous-cats` is `famous-cats`."
        )
        .max(100)
        .optional(),
      seoData: z
        .object({
          tags: z
            .array(
              z.object({
                type: z
                  .string()
                  .describe(
                    'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                  )
                  .optional(),
                props: z
                  .record(z.string(), z.any())
                  .describe(
                    'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                  )
                  .optional()
                  .nullable(),
                meta: z
                  .record(z.string(), z.any())
                  .describe(
                    'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                  )
                  .optional()
                  .nullable(),
                children: z
                  .string()
                  .describe(
                    'SEO tag inner content. For example, `<title> inner content </title>`.'
                  )
                  .optional(),
                custom: z
                  .boolean()
                  .describe(
                    'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                  )
                  .optional(),
                disabled: z
                  .boolean()
                  .describe(
                    "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                  )
                  .optional(),
              })
            )
            .optional(),
          settings: z
            .object({
              preventAutoRedirect: z
                .boolean()
                .describe(
                  'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                )
                .optional(),
              keywords: z
                .array(
                  z.object({
                    term: z.string().describe('Keyword value.').optional(),
                    isMain: z
                      .boolean()
                      .describe(
                        'Whether the keyword is the main focus keyword.'
                      )
                      .optional(),
                    origin: z
                      .string()
                      .describe(
                        'The source that added the keyword terms to the SEO settings.'
                      )
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe('SEO general settings.')
            .optional(),
        })
        .describe('SEO data.')
        .optional(),
      coverImage: z.string().describe('Category cover image.').optional(),
      _updatedDate: z
        .date()
        .describe('Date and time the Category was last updated.')
        .optional()
        .nullable(),
    })
    .describe('Category info.')
    .optional(),
});
export const ListCategoriesRequest = z.object({
  options: z
    .object({
      paging: z
        .object({
          offset: z
            .number()
            .int()
            .describe(
              'Number of items to skip in the current sort order.\n\n\nDefault: `0`'
            )
            .min(0)
            .optional(),
          limit: z
            .number()
            .int()
            .describe('Number of items to return.\n\n\nDefault:`50`')
            .min(1)
            .max(100)
            .optional(),
          cursor: z
            .string()
            .describe(
              'Pointer to the next or previous page in the list of results.'
            )
            .max(2000)
            .optional()
            .nullable(),
        })
        .describe('Pagination options.')
        .optional(),
      fieldsets: z
        .array(z.enum(['UNKNOWN', 'URL', 'SEO']))
        .max(10)
        .optional(),
      language: z
        .string()
        .describe(
          'Language filter.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nPass a language to only receive categories that are in that language.\nIf omitted, categories in all languages are returned.'
        )
        .optional()
        .nullable(),
    })
    .describe('Filter and paging options.')
    .optional(),
});
export const ListCategoriesResponse = z.object({
  categories: z
    .array(
      z.object({
        _id: z.string().describe('Category ID.').max(38).optional(),
        label: z
          .string()
          .describe('Category label. Displayed in the Category Menu.')
          .max(35)
          .optional(),
        postCount: z
          .number()
          .int()
          .describe('Number of posts in the category.')
          .optional(),
        url: z
          .string()
          .describe(
            'The `url` of the page that lists every post with the specified category.'
          )
          .optional(),
        description: z
          .string()
          .describe('Category description.')
          .max(500)
          .optional()
          .nullable(),
        title: z.string().describe('Category title.').max(200).optional(),
        displayPosition: z
          .number()
          .int()
          .describe(
            'Position of the category in the [Category Menu](https://support.wix.com/en/article/wix-blog-adding-and-customizing-a-category-menu).\nCategories are displayed in ascending order. Categories with a position of `-1` appear at the end of the sequence.\n\nDefault: `-1`'
          )
          .min(0)
          .optional()
          .nullable(),
        translationId: z
          .string()
          .describe(
            "ID of the category's translations. All translations of a single category share the same `translationId`."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        language: z
          .string()
          .describe(
            'Category language.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
          )
          .optional()
          .nullable(),
        slug: z
          .string()
          .describe(
            "Part of a category's URL that refers to a specific category. For example, the slug of `https:/example.com/blog/category/famous-cats` is `famous-cats`."
          )
          .max(100)
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('SEO data.')
          .optional(),
        coverImage: z.string().describe('Category cover image.').optional(),
        _updatedDate: z
          .date()
          .describe('Date and time the Category was last updated.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metaData: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in this response.')
        .optional(),
      offset: z.number().int().describe('Requested offset.').optional(),
      total: z
        .number()
        .int()
        .describe('Total number of items that match the query.')
        .optional(),
      cursor: z
        .string()
        .describe(
          'Pointer to the next or previous page in the list of results.'
        )
        .max(2000)
        .optional()
        .nullable(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const QueryCategoriesRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          title: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          label: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          postCount: z
            .object({
              $eq: z.number(),
              $gt: z.number(),
              $gte: z.number(),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
            })
            .partial()
            .strict()
            .optional(),
          displayPosition: z
            .object({
              $eq: z.number(),
              $gt: z.number(),
              $gte: z.number(),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
            })
            .partial()
            .strict()
            .optional(),
          translationId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          language: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          slug: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                'title',
                'label',
                'postCount',
                'displayPosition',
                'language',
                'slug',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
  options: z
    .object({
      fieldsets: z
        .array(z.enum(['UNKNOWN', 'URL', 'SEO']))
        .max(10)
        .optional(),
    })
    .describe('Options specifying which fields to return.')
    .optional(),
});
export const QueryCategoriesResponse = z.object({
  categories: z
    .array(
      z.object({
        _id: z.string().describe('Category ID.').max(38).optional(),
        label: z
          .string()
          .describe('Category label. Displayed in the Category Menu.')
          .max(35)
          .optional(),
        postCount: z
          .number()
          .int()
          .describe('Number of posts in the category.')
          .optional(),
        url: z
          .string()
          .describe(
            'The `url` of the page that lists every post with the specified category.'
          )
          .optional(),
        description: z
          .string()
          .describe('Category description.')
          .max(500)
          .optional()
          .nullable(),
        title: z.string().describe('Category title.').max(200).optional(),
        displayPosition: z
          .number()
          .int()
          .describe(
            'Position of the category in the [Category Menu](https://support.wix.com/en/article/wix-blog-adding-and-customizing-a-category-menu).\nCategories are displayed in ascending order. Categories with a position of `-1` appear at the end of the sequence.\n\nDefault: `-1`'
          )
          .min(0)
          .optional()
          .nullable(),
        translationId: z
          .string()
          .describe(
            "ID of the category's translations. All translations of a single category share the same `translationId`."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        language: z
          .string()
          .describe(
            'Category language.\n\n2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.'
          )
          .optional()
          .nullable(),
        slug: z
          .string()
          .describe(
            "Part of a category's URL that refers to a specific category. For example, the slug of `https:/example.com/blog/category/famous-cats` is `famous-cats`."
          )
          .max(100)
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('SEO data.')
          .optional(),
        coverImage: z.string().describe('Category cover image.').optional(),
        _updatedDate: z
          .date()
          .describe('Date and time the Category was last updated.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(2000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(2000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const DeleteCategoryRequest = z.object({
  categoryId: z.string().describe('Category ID.').min(1).max(38),
});
export const DeleteCategoryResponse = z.object({});
